const { useEffect, useState, useRef } = React;

function DualAudience() {
    const [isVisible, setIsVisible] = useState(false);
    const sectionRef = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                }
            },
            { threshold: 0.2 }
        );

        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }

        return () => observer.disconnect();
    }, []);

    return (
        <section id="dual-audience" ref={sectionRef} className="section-shell relative py-20 sm:py-24 px-4 sm:px-6 lg:px-8 bg-transparent overflow-hidden">
            {/* Background Elements */}
            <div className="absolute inset-0">
                <div className="absolute top-0 left-0 w-96 h-96 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-10"></div>
                <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-100 rounded-full mix-blend-multiply filter blur-3xl opacity-10"></div>
            </div>

            <div className="max-w-6xl mx-auto relative z-10">
                {/* Section Header */}
                <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                    <div className="section-kicker mx-auto mb-4 w-fit">For everyone</div>
                    <h2 className="text-4xl sm:text-5xl font-bold mb-6 text-gray-900 section-title">
                        Built for <span className="bg-gradient-to-r from-blue-600 to-blue-400 bg-clip-text text-transparent">Everyone</span>
                    </h2>
                    <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto">
                        A focused experience for clients who want clarity and realtors who want results.
                    </p>
                    <div className="w-20 h-1 bg-gradient-to-r from-blue-600 to-blue-400 mx-auto"></div>
                </div>

                {/* Split Screen Layout */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                    {/* For Clients */}
                    <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
                        <div className="relative">
                            {/* Glow Effect */}
                            <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 to-blue-400/10 rounded-3xl blur-2xl"></div>

                            {/* Card */}
                            <div className="relative surface-card p-7 sm:p-10 hover:border-blue-300/80 transition-all duration-500 group hover:shadow-2xl hover:shadow-blue-500/10">
                                {/* Icon */}
                                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-600/20 to-blue-400/10 flex items-center justify-center mb-8 border border-blue-300/30">
                                    <svg className="w-8 h-8 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M10 12a2 2 0 100-4 2 2 0 000 4z"></path>
                                        <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"></path>
                                    </svg>
                                </div>

                                {/* Title */}
                                <h3 className="text-3xl font-bold text-gray-900 mb-6">For Clients</h3>

                                {/* Description */}
                                <p className="text-gray-700 mb-8 leading-relaxed">
                                    Find your dream property and schedule viewings in minutes. Compare options, connect with verified realtors, and make informed decisions.
                                </p>

                                {/* Features List */}
                                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-10">
                                    {[
                                        'Browse thousands of listings',
                                        'Compare prices and amenities',
                                        'Book viewings instantly',
                                        'Chat with verified realtors',
                                        'Get property insights',
                                        'Secure transaction process'
                                    ].map((feature, index) => (
                                        <li key={index} className="flex items-center gap-3">
                                            <div className="w-5 h-5 rounded-full bg-gradient-to-r from-blue-600 to-blue-400 flex items-center justify-center flex-shrink-0">
                                                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                </svg>
                                            </div>
                                            <span className="text-gray-700 font-medium">{feature}</span>
                                        </li>
                                    ))}
                                </ul>

                                {/* CTA Button */}
                                <a
                                    href="customer.html"
                                    className="btn-primary w-full justify-center"
                                >
                                    Get Started as a Client
                                </a>
                            </div>
                        </div>
                    </div>

                    {/* For Realtors */}
                    <div className={`transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
                        <div className="relative">
                            {/* Glow Effect */}
                            <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 to-blue-400/10 rounded-3xl blur-2xl"></div>

                            {/* Card */}
                            <div className="relative surface-card p-7 sm:p-10 hover:border-blue-300/80 transition-all duration-500 hover:shadow-2xl hover:shadow-blue-500/10">
                                {/* Icon */}
                                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-600/20 to-blue-400/10 flex items-center justify-center mb-8 border border-blue-300/30">
                                    <svg className="w-8 h-8 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M10.5 1.5H5.75A2.25 2.25 0 003.5 3.75v12.5A2.25 2.25 0 005.75 18.5h8.5a2.25 2.25 0 002.25-2.25V6.5" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                                        <path d="M7 10h6M7 14h6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                    </svg>
                                </div>

                                {/* Title */}
                                <h3 className="text-3xl font-bold text-gray-900 mb-6">For Realtors</h3>

                                {/* Description */}
                                <p className="text-gray-700 mb-8 leading-relaxed">
                                    Manage your listings, appointments, and client interactions efficiently. Reach more buyers and sellers with our platform.
                                </p>

                                {/* Features List */}
                                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-10">
                                    {[
                                        'Upload and manage listings',
                                        'Schedule and track viewings',
                                        'Manage client appointments',
                                        'View engagement analytics',
                                        'Direct client communication',
                                        'Performance dashboard'
                                    ].map((feature, index) => (
                                        <li key={index} className="flex items-center gap-3">
                                            <div className="w-5 h-5 rounded-full bg-gradient-to-r from-blue-600 to-blue-400 flex items-center justify-center flex-shrink-0">
                                                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                </svg>
                                            </div>
                                            <span className="text-gray-700 font-medium">{feature}</span>
                                        </li>
                                    ))}
                                </ul>

                                {/* CTA Button */}
                                <a
                                    href="agent.html"
                                    className="btn-secondary w-full justify-center"
                                >
                                    Get Started as a Realtor
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

export default DualAudience;
